import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiSend, FiFilm, FiClock, FiCheck, FiX } from 'react-icons/fi';
import Footer from '../components/Footer';

const STATUS_STYLE = {
  pending:  { bg: 'rgba(245,197,24,0.15)',  color: '#F5C518',  label: 'Pending',   icon: FiClock },
  approved: { bg: 'rgba(0,200,100,0.12)',   color: '#00C864',  label: 'Approved',  icon: FiCheck },
  rejected: { bg: 'rgba(229,9,20,0.12)',    color: 'var(--red)', label: 'Rejected', icon: FiX },
};

const RequestPage = () => {
  const { user, requestMovie } = useAuth();
  const [title,      setTitle]      = useState('');
  const [note,       setNote]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent,       setSent]       = useState(false);
  const [error,      setError]      = useState('');
  const [myReqs,     setMyReqs]     = useState([]);

  // Load user's own requests
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'movieRequests'),
      where('uid', '==', user.uid),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, snap => setMyReqs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Please enter a movie title.'); return; }
    setError(''); setSubmitting(true);
    try {
      await requestMovie(title.trim(), note.trim());
      setTitle(''); setNote(''); setSent(true);
      setTimeout(() => setSent(false), 4000);
    } catch { setError('Failed to send request. Try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{ background: 'var(--dark)', minHeight: '100vh', paddingTop: 88 }}>
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-10">

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <FiFilm size={26} style={{ color: 'var(--red)' }} />
            <h1 className="font-display text-4xl md:text-5xl tracking-wide" style={{ color: 'var(--text)' }}>Request a Movie</h1>
          </div>
          <p className="font-body text-sm" style={{ color: 'var(--muted)' }}>
            Can't find what you're looking for? Let us know and we'll try to add it.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="rounded-2xl p-7 mb-8"
          style={{ background: 'rgba(17,17,24,0.88)', border: '1px solid rgba(255,255,255,0.07)' }}>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="text-xs font-accent uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--muted)' }}>
                Movie / Series Title *
              </label>
              <input value={title} onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Interstellar"
                className="w-full px-4 py-3 rounded-xl text-sm font-body outline-none transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--text)' }}
                onFocus={e => e.target.style.borderColor = 'rgba(229,9,20,0.5)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
            </div>
            <div>
              <label className="text-xs font-accent uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--muted)' }}>
                Additional Notes (optional)
              </label>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                placeholder="Year, director, why you'd like it added…"
                className="w-full px-4 py-3 rounded-xl text-sm font-body outline-none resize-none transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--text)' }}
                onFocus={e => e.target.style.borderColor = 'rgba(229,9,20,0.5)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
            </div>

            <AnimatePresence>
              {error && (
                <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="text-xs font-body px-3 py-2 rounded-lg"
                  style={{ color: '#ff6b6b', background: 'rgba(229,9,20,0.1)' }}>{error}</motion.p>
              )}
              {sent && (
                <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="text-xs font-body px-3 py-2 rounded-lg"
                  style={{ color: '#6bff9e', background: 'rgba(0,200,100,0.1)' }}>
                  ✓ Request submitted! We'll review it soon.
                </motion.p>
              )}
            </AnimatePresence>

            <motion.button type="submit" disabled={submitting}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              className="flex items-center justify-center gap-2 py-3 rounded-xl font-body font-semibold text-sm"
              style={{ background: 'var(--red)', color: '#fff', boxShadow: '0 0 24px rgba(229,9,20,0.3)', opacity: submitting ? 0.6 : 1 }}>
              <FiSend size={15} /> {submitting ? 'Sending…' : 'Submit Request'}
            </motion.button>
          </form>
        </motion.div>

        {/* My requests history */}
        {myReqs.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-display text-2xl tracking-wide mb-4" style={{ color: 'var(--text)' }}>My Requests</h2>
            <div className="flex flex-col gap-3">
              {myReqs.map((req, i) => {
                const s = STATUS_STYLE[req.status] || STATUS_STYLE.pending;
                return (
                  <motion.div key={req.id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="flex items-center justify-between gap-4 rounded-xl px-5 py-4"
                    style={{ background: 'rgba(17,17,24,0.7)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FiFilm size={16} style={{ color: 'var(--muted)', flexShrink: 0 }} />
                      <div className="min-w-0">
                        <p className="text-sm font-body font-semibold truncate" style={{ color: 'var(--text)' }}>{req.title}</p>
                        {req.note && <p className="text-xs truncate mt-0.5" style={{ color: 'var(--muted)' }}>{req.note}</p>}
                        <p className="text-xs mt-0.5 font-accent" style={{ color: 'var(--muted)', opacity: 0.6 }}>
                          {req.createdAt ? new Date(req.createdAt.seconds * 1000).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 px-3 py-1.5 rounded-full flex items-center gap-1.5"
                      style={{ background: s.bg }}>
                      <s.icon size={11} style={{ color: s.color }} />
                      <span className="text-xs font-accent" style={{ color: s.color }}>{s.label}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default RequestPage;
